import { isWeb } from "@helpers/platform";
/* -----------------Globals--------------- */
import { useCallback, useState } from "react";

import { images } from "@helpers/images";
import { Box, FAB, useToast } from "@memorang/ui";
import type { IconType } from "components/index";
/* -----------------UI--------------- */
import { Drawer } from "../../../CustomDrawer";

import { useExamContext } from "@features/exam";
import { useSession } from "@features/session";
/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";

import { getShowStudyPackViews } from "@helpers/exams";
import { currentAppVersion } from "@helpers/expo-extras";
import i18n from "@helpers/lib/i18n";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useAppTheme } from "@hooks/useAppTheme";
import useBreakpoints from "@hooks/useBreakpoints";
import useClerkUser from "@hooks/useClerkUser";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { DrawerContents, type Section } from "components/DrawerContents";
import * as Localization from "expo-localization";
import type { Href } from "expo-router";
import { useTranslation } from "react-i18next";
import { Platform, ScrollView } from "react-native";
import { PurchaseDialog } from "../components/PurchaseDialog";
import { QuickStudyDialog } from "../components/QuickStudyDialog";
import { QuickStudyButtonSkeleton } from "../components/Skeletons/QuickStudyButtonSkeleton";
import { drawerWidths } from "../constants";
import {
	type AppLaunchParams,
	handleNotifyUserOnAppLaunch,
} from "../helpers/app-launch";

type NavigationTab = {
	name: string;
	enabled: boolean;
	icon: {
		selected: string;
		default: string;
	};
};

const addProethicaNavItems = (
	items: Section["items"],
	modulesLabel: string,
	principlesLabel: string,
) => {
	const homeIndex = items.findIndex((item) => String(item.route) === "/home");
	const insertAt = homeIndex >= 0 ? homeIndex + 1 : 1;
	items.splice(
		insertAt,
		0,
		{
			label: modulesLabel,
			focusedIcon: "book-open-variant",
			unfocusedIcon: "book-open-variant-outline",
			route: "/modules",
		},
		{
			label: principlesLabel,
			focusedIcon: "scale-balance",
			unfocusedIcon: "scale-balance",
			route: "/principles",
		},
	);
};

const getDrawerSections = (
	tabs: NavigationTab[],
	translate: (key: string) => string,
	demoExamId: string,
): Section[] => {
	const sections: Section[] = [
		{
			title: "",
			items: tabs
				.filter((item) => item.name !== "profile" && item.enabled)
				.filter(
					(item) =>
						!(demoExamId === "proethica" && item.name === "leaderboard"),
				)
				.map((item) => ({
					label: translate(`navbar.${item.name}`),
					focusedIcon: item.icon.selected as IconType,
					unfocusedIcon: item.icon.default as IconType,
					route: `/${item.name}` as Href,
				})),
		},
	];

	if (demoExamId === "proethica") {
		addProethicaNavItems(
			sections[0].items,
			translate("navbar.modules"),
			translate("navbar.principles"),
		);
	}

	return sections;
};

export const AppDrawer = () => {
	const appName = useAppStore((state) => state.app.name);
	const appAvailabilityRegions = useAppStore(
		(state) => state.app.appAvailabilityRegions,
	);

	const regionCode = Localization.getLocales()[0]?.regionCode ?? "US";

	const isAppAvailableInCurrentRegion =
		appAvailabilityRegions?.includes(regionCode);

	const ios = useAppStore((state) => state.app.links.ios);
	const android = useAppStore((state) => state.app.links.android);
	const iosLocalised = useAppStore((state) => state.app.links.iosLocalised);
	const androidLocalised = useAppStore(
		(state) => state.app.links.androidLocalised,
	);
	const appLogo = useAppStore((state) => state.app.logo);
	const companyName = useAppStore((state) => state.companyName);
	const tabs = useAppStore((state) => state.navigation.routes.main);

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const { projectId, organizationId, productId } = useAppConfigIds();

	const { username, email } = useClerkUser();

	const notifyAppLaunchParams = {
		userName: username || "",
		email: email!,
		appName,
		examName,
		appLanguage: i18n.language,
		companyName,
		type: "Notify app launch" as const,
		projectId,
		organizationId,
		appVersion: currentAppVersion ?? "",
		bundleId: productId.toString(),
		platform: Platform.OS,
		feedback: "Subscribing to app launch",
	} satisfies AppLaunchParams;

	const { handleDirectSessionStart, isCreatingSession } = useSession();
	const userAvatar = "";
	const {
		currentExamContext: { studyPackProductId, examId },
	} = useExamContext();

	const { showToast } = useToast();

	const showStudyPackViews = getShowStudyPackViews(studyPackProductId);

	const { t } = useTranslation();
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const { smUp, mdDown } = useBreakpoints();
	const [showProethicaQuickStudy, setShowProethicaQuickStudy] = useState(false);
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
	const theme = useAppTheme();
	const { data: mixedItemDist } = useMixedItemDist();
	const hasAccess = mixedItemDist?.hasAccess ?? false;
	const isCollapsed = smUp && mdDown;
	const leftDrawerWidth = isCollapsed
		? drawerWidths.collapsed
		: drawerWidths.expanded;
	const drawerType = smUp ? "permanent" : "slide";

	const getLink = useCallback(
		(links: Record<string, string | null>, defaultLink?: string) => {
			const locale = Localization.getLocales()[0];
			const { languageTag, regionCode } = locale;
			if (links?.[languageTag] !== undefined) {
				return links[languageTag];
			}
			const region = Object.keys(links ?? {}).find((key) =>
				key.includes(regionCode ?? "US"),
			);

			if (region && links?.[region] !== undefined) {
				return links[region];
			}
			return defaultLink;
		},
		[],
	);

	const sections = getDrawerSections(tabs, t, demoExamId);

	if (isWeb) {
		// if (practiceTestProductId) {
		// 	sections[0].items.splice(1, 0, {
		// 		label: "Practice Tests",
		// 		focusedIcon: "clipboard-check",
		// 		unfocusedIcon: "clipboard-check-outline",
		// 		route: "/practice-tests",
		// 	});
		// }
		// if (showStudyPackViews && !showStudyPackAsHome) {
		// 	sections[0].items.splice(2, 0, {
		// 		label: "Study-Pack",
		// 		focusedIcon: "study-pack",
		// 		unfocusedIcon: "study-pack-outline",
		// 		route: "/study-pack",
		// 		custom: true,
		// 	});
		// }

		if (isWeb && demoExamId !== "proethica") {
			sections[0].items.push({
				label: "Redeem",
				focusedIcon: "gift",
				unfocusedIcon: "gift-outline",
				route: "/redeem?step=redeem",
			});
		}
	}
	const QuickStudyButton = () => {
		if (!examId) {
			return <QuickStudyButtonSkeleton />;
		}
		return (
			showStudyPackViews && (
				<Box paddingBottom={16} paddingTop={8} alignSelf="baseline">
					<FAB
						icon={"play-outline"}
						variant="primary"
						loading={isCreatingSession}
						disabled={isCreatingSession}
						onPress={() => {
							if (demoExamId === "proethica") {
								setShowProethicaQuickStudy(true);
								return;
							}
							handleDirectSessionStart();
						}}
						label={isCollapsed ? "" : t("dashboard.header.quickStudy")}
						testID="quick-study-button"
					/>
				</Box>
			)
		);
	};

	return (
		<>
			{showProethicaQuickStudy && (
				<QuickStudyDialog
					visible={showProethicaQuickStudy}
					handleClose={() => setShowProethicaQuickStudy(false)}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setShowPurchaseDialog(false)}
				/>
			)}
			<Drawer
				drawerContent={(props) => (
					<ScrollView
						showsVerticalScrollIndicator={false}
						contentContainerStyle={{
							flexGrow: 1,
						}}
					>
						<DrawerContents
							drawerHeader={{
								appLogo: appLogo,
								renderFab: () => {
									return <QuickStudyButton />;
								},
							}}
							{...props}
							sections={sections}
							isCollapsed={isCollapsed}
							footer={{
								userName: "",
								userAvatar,
								showUnlockPremiumWidget:
									demoExamId === "proethica" && !hasAccess,
								onUnlockPremiumWidgetPress: () => setShowPurchaseDialog(true),

								...(demoExamId !== "proethica" && {
									storeDownloadBadge: {
										ios: {
											badge: images.appStores.ios,
											href: getLink(iosLocalised ?? {}, ios ?? "") ?? null,
										},
										android: {
											badge: images.appStores.android,
											href:
												getLink(androidLocalised ?? {}, android ?? "") ?? null,
										},
									},
								}),
								showNotifyDialog: Boolean(!isAppAvailableInCurrentRegion),
								notifyDialogTitle: t("dialog.notifyAppLaunch.title"),
								notifyDialogBody: t("dialog.notifyAppLaunch.body", {
									appName,
								}),
								notifyDialogCta: t("dialog.notifyAppLaunch.cta"),
								handleNotifyDialogCtaPress: () => {
									handleNotifyUserOnAppLaunch(notifyAppLaunchParams);
									showToast({
										message: t("dialog.notifyAppLaunch.successMessage", {
											appName,
										}),
									});
								},
							}}
						/>
					</ScrollView>
				)}
				screenOptions={{
					drawerType: drawerType,
					headerTitle: "",
					drawerStyle: {
						width: leftDrawerWidth,
						borderRightColor:
							demoExamId === "proethica"
								? theme.colors.outlineVariant
								: "transparent",
						borderRightWidth: demoExamId === "proethica" ? 1 : 0,
					},
					header: () => null,
				}}
			/>
		</>
	);
};
